<template>
  <SearchAndFilterWrapper
    :search-label="'Veranstaltungen suchen...'"
    :dialog-title="'Filter Veranstaltungen'"
    :applied-filters="appliedFilters"
    :apply-filters-fn="applyFilters"
    :reset-pending-fn="resetPending"
    :remove-applied-filter-fn="removeAppliedFilter"
    :clear-all-fn="clearAll"
    @update:search="handleSearch"
  >
    <template v-slot:content>
      <div class="mb-4">
        <div class="text-subtitle-1 mb-2">Tags</div>
        <v-select
          v-model="pending.selectedTags"
          :items="api.tags"
          placeholder="Tags auswählen"
          hide-selected
          hide-details
          no-data-text="Keine Filtermöglichkeiten gefunden"
          multiple
          clearable
          chips
          variant="solo"
          density="comfortable"
        >
          <template v-slot:chip="{ item, index, props }">
            <v-chip
              v-bind="props"
              :key="index"
              closable
              color="secondary"
              size="small"
              variant="flat"
              @click.stop
              @click:close.stop="removePendingTag(item)"
              class="me-1"
            >
              <span>{{ item?.title }}</span>
            </v-chip>
          </template>
        </v-select>
      </div>
      <div class="mb-4">
        <v-row>
          <v-col cols="12" sm="6">
            <div class="text-subtitle-1 mb-2">Öffentlich</div>
            <v-select
              v-model="pending.isPublic"
              :items="booleanOptions"
              placeholder="Öffentlich auswählen"
              variant="solo"
              density="comfortable"
              clearable
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6">
            <div class="text-subtitle-1 mb-2">Bezahlte Events</div>
            <v-select
              v-model="pending.isPaid"
              :items="booleanOptions"
              placeholder="Bezahlte Events"
              variant="solo"
              density="comfortable"
              clearable
              hide-details
            ></v-select>
          </v-col>
        </v-row>
      </div>
      <div class="mb-4">
        <v-row class="align-center">
          <v-col cols="12" sm="6">
            <div class="text-subtitle-1 mb-2">Registrierung benötigt</div>
            <v-select
              v-model="pending.needsRegistration"
              :items="booleanOptions"
              placeholder="Registrierung"
              variant="solo"
              density="comfortable"
              clearable
              hide-details
            ></v-select>
          </v-col>
          <v-col cols="12" sm="6" class="mt-3 pt-3">
            <v-checkbox
              class="mt-5"
              v-model="pending.hidePastEvents"
              label="Vergangene Ereignisse ausblenden"
              hide-details
            ></v-checkbox>
          </v-col>
        </v-row>
      </div>
      <div>
        <div class="text-subtitle-1 mb-2">Zeitraum</div>
        <v-row>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menuValidFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              min-width="auto"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="pending.validFrom"
                  placeholder="Von"
                  hide-details
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                  variant="solo"
                  density="comfortable"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="pending.validFrom"
                hide-title
                @update:modelValue="menuValidFrom = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6">
            <v-menu
              v-model="menuValidTo"
              :close-on-content-click="false"
              transition="scale-transition"
              min-width="auto"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="pending.validTo"
                  placeholder="Bis"
                  hide-details
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                  variant="solo"
                  density="comfortable"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="pending.validTo"
                hide-title
                @update:modelValue="menuValidTo = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
      </div>
    </template>
  </SearchAndFilterWrapper>
</template>

<script>
import ApiEventService from "../../services/api/ApiEventService";
import SearchAndFilterWrapper from "@/components/Filters/SearchAndFilterWrapper.vue";
import { mapGetters } from "vuex";

export default {
  name: "EventSearchAndFilter",
  components: { SearchAndFilterWrapper },
  data() {
    return {
      menuValidFrom: false,
      menuValidTo: false,
      pending: {
        selectedTags: [],
        isPublic: null,
        isPaid: null,
        needsRegistration: null,
        hidePastEvents: false,
        validFrom: null,
        validTo: null,
      },
      applied: {},
      api: { tags: [] },
      booleanOptions: [
        { title: "Ja", value: true },
        { title: "Nein", value: false },
      ],
    };
  },
  watch: {
    async tenantId() {
      await this.fetchFilterTags();
    },
    "$route.query": {
      deep: true,
      handler(newQuery) {
        const filters = this.queryToFilters(newQuery);
        this.applied = JSON.parse(JSON.stringify(filters));
        this.pending = JSON.parse(JSON.stringify(filters));
        this.$emit("update:filters", this.applied);
      },
    },
  },
  computed: {
    ...mapGetters({
      tenantId: "tenants/currentTenantId",
    }),
    appliedFilters() {
      const chips = [];
      if (this.applied.selectedTags?.length)
        this.applied.selectedTags.forEach((tag) => {
          chips.push({ type: "tag", value: tag, label: `Tag: ${tag}` });
        });
      if (this.applied.isPublic !== null)
        chips.push({
          type: "isPublic",
          label: `Ist Öffentlich: ${this.applied.isPublic ? "Ja" : "Nein"}`,
        });
      if (this.applied.isPaid !== null)
        chips.push({
          type: "isPaid",
          label: `Bezahlte Events: ${this.applied.isPaid ? "Ja" : "Nein"}`,
        });
      if (this.applied.needsRegistration !== null)
        chips.push({
          type: "needsRegistration",
          label: `Registrierung: ${
            this.applied.needsRegistration ? "Ja" : "Nein"
          }`,
        });
      if (this.applied.hidePastEvents)
        chips.push({
          type: "hidePastEvents",
          label: "Vergangene Ereignisse ausblenden: Ja",
        });
      if (this.applied.validFrom || this.applied.validTo) {
        const from = this.applied.validFrom;
        const to = this.applied.validTo;
        chips.push({
          type: "date",
          label: `Datum: ${
            from
              ? Intl.DateTimeFormat("de-DE", {
                  dateStyle: "short",
                }).format(new Date(from))
              : "-"
          } bis ${
            to
              ? Intl.DateTimeFormat("de-DE", {
                  dateStyle: "short",
                }).format(new Date(to))
              : "-"
          }`,
        });
      }
      return chips;
    },
  },
  methods: {
    async fetchFilterTags() {
      ApiEventService.getTags()
        .then((response) => (this.api.tags = response.data))
        .catch((error) => console.log(error));
    },
    removePendingTag(tag) {
      this.pending.selectedTags = this.pending.selectedTags.filter(
        (t) => t !== tag
      );
    },
    filtersToQuery(filters) {
      const query = {};
      if (filters.selectedTags?.length)
        query.tags = filters.selectedTags.join(",");
      if (filters.isPublic !== null) query.isPublic = filters.isPublic;
      if (filters.isPaid !== null) query.isPaid = filters.isPaid;
      if (filters.needsRegistration !== null)
        query.needsRegistration = filters.needsRegistration;
      if (filters.hidePastEvents) query.hidePastEvents = filters.hidePastEvents;
      if (filters.validFrom) query.validFrom = filters.validFrom;
      if (filters.validTo) query.validTo = filters.validTo;
      return query;
    },
    queryToFilters(query) {
      return {
        selectedTags: query.tags ? query.tags.split(",") : [],
        isPublic:
          query.isPublic !== undefined
            ? query.isPublic === "true" || query.isPublic === true
            : null,
        isPaid:
          query.isPaid !== undefined
            ? query.isPaid === "true" || query.isPaid === true
            : null,
        needsRegistration:
          query.needsRegistration !== undefined
            ? query.needsRegistration === "true" ||
              query.needsRegistration === true
            : null,
        hidePastEvents:
          query.hidePastEvents === "true" || query.hidePastEvents === true,
        validFrom: query.validFrom || null,
        validTo: query.validTo || null,
      };
    },
    updateUrlFromFilters(filters) {
      const query = this.filtersToQuery(filters);
      this.$router.replace({ query });
    },
    removeAppliedFilter(chip) {
      if (chip.type === "tag")
        this.applied.selectedTags = this.applied.selectedTags.filter(
          (t) => t !== chip.value
        );
      else if (chip.type === "date") {
        this.applied.validFrom = null;
        this.applied.validTo = null;
      } else this.applied[chip.type] = null;

      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    applyFilters() {
      this.applied = JSON.parse(JSON.stringify(this.pending));
      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    clearAll() {
      this.pending = {
        tags: [],
        isPublic: null,
        isPaid: null,
        needsRegistration: null,
        hidePastEvents: false,
        validFrom: null,
        validTo: null,
      };
      this.applied = JSON.parse(JSON.stringify(this.pending));
      this.$emit("update:filters", this.applied);
      this.updateUrlFromFilters(this.applied);
    },
    resetPending() {
      this.pending = JSON.parse(JSON.stringify(this.applied));
    },
    handleSearch(value) {
      this.$emit("update:search", value);
    },
  },
  mounted() {
    this.fetchFilterTags();

    const initialFilters = this.queryToFilters(this.$route.query);
    this.applied = JSON.parse(JSON.stringify(initialFilters));
    this.pending = JSON.parse(JSON.stringify(initialFilters));
    this.$emit("update:filters", this.applied);
  },
};
</script>

<style scoped lang="scss">
::v-deep .active-button {
  color: white !important;
  background-color: #0099db !important;
}
</style>
